import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { BaseService } from '@core';
import { ActiveStatusEnum } from '@models';

import {
  ShippingTransferApiService,
  ShippingTransferBoardDto,
  ShippingTransferTileDto,
  ShippingTransferUserDto,
} from '../services/shipping-transfer-api.service';
import { ProgramFrmComponent } from './program-frm.component';

/**
 * Shipping Group Transfer — the Program User Group board, pointed at a much simpler model.
 *
 * A ShippingUserGroupMap is two columns and carries no program, so there is no "which group
 * governs this program" question, no exclusions, and no usage to carry forward. A transfer
 * deletes the user's existing memberships and adds one.
 *
 * THAT SIMPLICITY IS ALSO THE DANGER. Those deletes are permanent — there is no shipping
 * equivalent of an exclusion to lift afterwards — and 773 users on the dev account belong to more
 * than one group. So the roster badges anyone with more than one, and the confirm names exactly
 * what each of them is about to lose. Collapsing somebody's shipping options should be a choice,
 * not a discovery.
 *
 * SCOPE. Shipping groups are account-scoped. The roster is drawn from this program because that
 * is the list the administrator came in with, but the change follows the user into every program
 * they are in — which the panel states rather than leaving to be inferred.
 *
 * No locked users here. An order already carries its shipping address, so changing group
 * membership cannot strand one, and the release guard skips shipping deliberately.
 */
@Component({
  selector: 'prox-program-shipping-transfer-sub',
  templateUrl: './program-shipping-transfer.sub.component.html',
  standalone: false,
  styles: [
    `
      .roster {
        overflow-y: auto;
      }
      .roster-row {
        cursor: grab;
        border-bottom: 1px solid var(--surface-border);
      }
      .roster-row.selected {
        background: var(--p-primary-50);
      }
      /* Same loud treatment as the Program User Group board: a thin outline is easy to miss while
         a drag image is moving over it. */
      .tile-drop-target {
        outline: 3px solid var(--p-primary-color);
        outline-offset: -3px;
        background: var(--p-primary-50);
        box-shadow: 0 4px 12px rgb(0 0 0 / 15%);
        transform: translateY(-1px);
      }
      .tile-disabled {
        opacity: 0.55;
      }
    `,
  ],
})
export class ProgramShippingTransferSubComponent implements OnChanges {
  @Input() parent!: ProgramFrmComponent;
  @Input() visible!: boolean;

  board?: ShippingTransferBoardDto;
  selectedUserIds = new Set<string>();
  dragOverTileId?: string;

  /** Filters the roster by a group the user currently belongs to. Never touches the selection. */
  groupFilterId?: string;
  readonly NO_GROUP = '--none--';

  movedThisSession = new Set<string>();

  loading = false;
  transferring = false;
  loadError = '';
  lastResultMessage = '';

  private loadedProgramId?: string;

  constructor(
    private api: ShippingTransferApiService,
    private baseService: BaseService,
  ) {}

  async ngOnChanges(changes: SimpleChanges) {
    if (changes['visible'] && this.visible) {
      await this.refresh(false);
    }
  }

  async refresh(forceRefresh: boolean) {
    if (!this.visible) return;
    const programId = this.parent?.program?.id;
    if (!programId) return;
    if (!forceRefresh && this.loadedProgramId === programId && this.board) return;
    await this.loadBoard();
  }

  private async loadBoard() {
    const program = this.parent.program;
    this.loading = true;
    this.loadError = '';
    try {
      this.board = await this.api.getBoard(program.accountId, program.id).toPromise();
      this.loadedProgramId = program.id;
    } catch (err: any) {
      this.loadError = this.errorText(err, 'Could not load the transfer board.');
      this.board = undefined;
    } finally {
      this.loading = false;
    }
  }

  // ---- Roster -----------------------------------------------------------------------------

  isSelected(u: ShippingTransferUserDto): boolean {
    return this.selectedUserIds.has(u.Id);
  }

  toggleUser(u: ShippingTransferUserDto) {
    if (this.transferring) return;
    if (this.selectedUserIds.has(u.Id)) {
      this.selectedUserIds.delete(u.Id);
    } else {
      this.selectedUserIds.add(u.Id);
    }
  }

  /** Filtering is a view concern only — a tick made under one filter survives changing it. */
  get visibleUsers(): ShippingTransferUserDto[] {
    const users = this.board?.Users ?? [];
    if (!this.groupFilterId) return users;
    if (this.groupFilterId === this.NO_GROUP) return users.filter((u) => u.CurrentGroupIds.length === 0);
    return users.filter((u) => u.CurrentGroupIds.includes(this.groupFilterId!));
  }

  get groupFilterOptions(): { label: string; value: string }[] {
    const opts = (this.board?.Tiles ?? []).map((t) => ({ label: t.Name, value: t.Id }));
    if ((this.board?.Users ?? []).some((u) => u.CurrentGroupIds.length === 0)) {
      opts.push({ label: '(no shipping group)', value: this.NO_GROUP });
    }
    return opts;
  }

  get selectedCount(): number {
    return this.selectedUserIds.size;
  }

  get allSelectableSelected(): boolean {
    const visible = this.visibleUsers;
    return visible.length > 0 && visible.every((u) => this.selectedUserIds.has(u.Id));
  }

  toggleSelectAll() {
    if (this.allSelectableSelected) {
      this.selectedUserIds.clear();
    } else {
      this.visibleUsers.forEach((u) => this.selectedUserIds.add(u.Id));
    }
  }

  hasMoved(u: ShippingTransferUserDto): boolean {
    return this.movedThisSession.has(u.Id);
  }

  /** Users in more than one group — the ones a transfer takes something away from. */
  isMultiGroup(u: ShippingTransferUserDto): boolean {
    return u.CurrentGroupNames.length > 1;
  }

  get selectedNames(): string[] {
    const byId = new Map((this.board?.Users ?? []).map((u) => [u.Id, u.Name]));
    return Array.from(this.selectedUserIds)
      .map((id) => byId.get(id))
      .filter((n): n is string => n != null)
      .sort((a, b) => a.localeCompare(b));
  }

  get movingMessage(): string {
    const names = this.selectedNames;
    if (names.length === 0) return '';
    return `Moving ${names.length} ${names.length === 1 ? 'Account User' : 'Account Users'}: `
         + this.summarizeNames(names);
  }

  private summarizeNames(names: string[]): string {
    const shown = names.slice(0, 10).join('; ');
    const rest = names.length - 10;
    return rest > 0 ? `${shown} and ${rest} other${rest === 1 ? '' : 's'}` : shown;
  }

  // ---- Drag and drop ----------------------------------------------------------------------

  onDragStart(u: ShippingTransferUserDto) {
    if (!this.selectedUserIds.has(u.Id)) {
      this.selectedUserIds.clear();
      this.selectedUserIds.add(u.Id);
    }
  }

  onDragEnter(tile: ShippingTransferTileDto) {
    if (this.canReceive(tile)) this.dragOverTileId = tile.Id;
  }

  /** Clear only for the highlighted tile: a native dragleave fires on every child crossing. */
  onDragLeave(tile: ShippingTransferTileDto) {
    if (this.dragOverTileId === tile.Id) this.dragOverTileId = undefined;
  }

  onRosterDragEnd() {
    this.dragOverTileId = undefined;
  }

  canReceive(tile: ShippingTransferTileDto): boolean {
    return tile.CannotReceiveReason == null;
  }

  async onDrop(tile: ShippingTransferTileDto) {
    this.dragOverTileId = undefined;
    if (!this.canReceive(tile) || this.transferring) return;

    const ids = Array.from(this.selectedUserIds);
    if (ids.length === 0) return;

    // Everyone in this drop who is about to lose a group. This is the whole reason the confirm
    // exists even for a single user: the removals cannot be undone, so what is being given up has
    // to be named before it happens, not reported afterwards.
    const byId = new Map((this.board?.Users ?? []).map((u) => [u.Id, u]));
    const losing = ids
      .map((id) => byId.get(id))
      .filter((u): u is ShippingTransferUserDto => u != null)
      .map((u) => ({ u, lost: u.CurrentGroupNames.filter((n) => n !== tile.Name) }))
      .filter((x) => x.lost.length > 0);

    const needsConfirm = ids.length > 1 || losing.length > 0;
    if (needsConfirm) {
      let msg = `Transfer <b>${ids.length}</b> Account User${ids.length === 1 ? '' : 's'} to `
              + `Shipping Group <b>"${tile.Name}"</b>?`;
      if (losing.length > 0) {
        const lines = losing
          .slice(0, 10)
          .map((x) => `<li>${x.u.Name} — loses ${x.lost.join(', ')}</li>`)
          .join('');
        const more = losing.length > 10 ? `<li>and ${losing.length - 10} more</li>` : '';
        msg +=
          `<br/><br/>Shipping Groups are account-wide, so this applies to ${ids.length === 1 ? 'them' : 'them'} `
          + `across every program.<br/><br/><b>${losing.length}</b> of these will lose their existing `
          + `Shipping Group membership, which cannot be undone:<ul>${lines}${more}</ul>`;
      }
      const ok = await this.baseService.dialogService.verifyProceedDialog('Transfer Account Users?', msg);
      if (!ok) return;
    }

    await this.doTransfer(tile, ids);
  }

  private async doTransfer(tile: ShippingTransferTileDto, accountUserIds: string[]) {
    const program = this.parent.program;
    this.transferring = true;
    this.loadError = '';
    this.lastResultMessage = '';
    try {
      const r = await this.api
        .transfer({
          AccountId: program.accountId,
          ProgramId: program.id,
          TargetShippingUserGroupId: tile.Id,
          AccountUserIds: accountUserIds,
        })
        .toPromise();

      r!.TransferredUserIds.forEach((id) => this.movedThisSession.add(id));

      const names = r!.TransferredUserNames;
      const who = names.length > 0 ? this.summarizeNames(names) : 'nobody';
      const parts: string[] = [`Moved to ${r!.TargetGroupName}: ${who}`];
      if (r!.MapsDeleted > 0) {
        parts.push(`${r!.MapsDeleted} previous group membership${r!.MapsDeleted === 1 ? '' : 's'} removed`);
      }
      if (r!.UsersSkipped > 0) {
        parts.push(`${r!.UsersSkipped} skipped`);
      }
      this.lastResultMessage = parts.join(' · ');
      if (r!.Problems.length > 0) {
        this.loadError = r!.Problems.join('  ');
      }

      this.selectedUserIds.clear();
      await this.loadBoard();
    } catch (err: any) {
      this.loadError = this.errorText(err, 'The transfer failed. Nothing was applied.');
    } finally {
      this.transferring = false;
    }
  }

  // ---- Display ----------------------------------------------------------------------------

  addressSummary(tile: ShippingTransferTileDto): string {
    if (tile.AddressCount === 0) return 'none';
    const shown = tile.AddressNames.join(', ');
    const rest = tile.AddressCount - tile.AddressNames.length;
    return rest > 0 ? `${shown} and ${rest} more` : shown;
  }

  isInactive(tile: ShippingTransferTileDto): boolean {
    return tile.ActiveStatusId === ActiveStatusEnum.Inactive;
  }

  private errorText(err: any, fallback: string): string {
    return err?.error?.Message ?? err?.error ?? err?.message ?? fallback;
  }
}
